import { useGetDeviceTagsForTagsListQuery } from "graphqlBase/DeviceTag/__generated__/getDeviceTagsForTagsList";
import { useGetMappingDeviceTagsQuery } from "graphqlBase/MappingDeviceTag/__generated__/getMappingDeviceTags";
import { useGetTagsQuery } from "graphqlBase/Tag/__generated__/getTags";
import { useEffect } from "react";
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil";
import {
  deviceTagIdsToDeleteAtom,
  deviceTagsforTagsListAtom,
  deviceTagsToCreateAtom,
} from "../recoilState/deviceTagsState";
import { selectedDevicesForTagsAtom } from "../recoilState/selectedDevicesForTagsState";
import { assignedDeviceTagErrorAtom, deviceTagModalLoadingAtom } from "../recoilState/tagModalState";
import useCreateDeviceTags from "./useCreateDeviceTags";
import useDeleteDeviceTags from "./useDeleteDeviceTags";

interface UseHandleUpdateTagsProps {
  afterMutation: () => Promise<void>;
  loading: boolean;
  onClose: () => void;
  open: boolean;
}

const useHandleUpdateDeviceTags = ({
  afterMutation,
  loading,
  onClose,
  open,
}: UseHandleUpdateTagsProps & { onClose: () => void }) => {
  const [isLoading, setIsLoading] = useRecoilState(deviceTagModalLoadingAtom);
  const [deviceTagsForTagsList, setDeviceTagsForTagsList] = useRecoilState(deviceTagsforTagsListAtom);
  const tagIdsToDelete = useRecoilValue(deviceTagIdsToDeleteAtom);
  const resetCreateDeviceTags = useResetRecoilState(deviceTagsToCreateAtom);
  const resetTagIdsToDelete = useResetRecoilState(deviceTagIdsToDeleteAtom);
  const resetAssignedTagError = useResetRecoilState(assignedDeviceTagErrorAtom);
  const selectedDevices = useRecoilValue(selectedDevicesForTagsAtom);
  const selectedDeviceIds = selectedDevices?.map((device) => device.id);
  const { data: mappingDeviceTagsData, loading: mappingDeviceTagsLoading } = useGetMappingDeviceTagsQuery({
    fetchPolicy: "network-only",
  });

  const { createDeviceTagsLoading, handleCreateDeviceTags } = useCreateDeviceTags({
    selectedDeviceIds,
  });
  const { deleteDeviceTagsLoading, handleDeleteDeviceTags } = useDeleteDeviceTags({ selectedDeviceIds });

  const { data: deviceTagsData, loading: tagsLoading, refetch: refetchTags } = useGetTagsQuery();

  const {
    data: tagsForListData,
    loading: deviceTagsLoading,
    refetch: refetchTagsForLists,
  } = useGetDeviceTagsForTagsListQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      take: !open || !selectedDeviceIds?.length ? 0 : undefined,
      where: { deviceId: { in: selectedDeviceIds } },
    },
  });

  useEffect(() => {
    if (deviceTagsForTagsList !== undefined) return;
    if (!tagsForListData?.devices?.items?.length) return;

    const mappingDeviceTags = tagsForListData?.devices?.items?.flatMap((item) => item.mappingDeviceTag) ?? [];
    if (!mappingDeviceTags?.length) {
      setDeviceTagsForTagsList([]);
      return;
    }

    const tagCountMap: { [key: string]: number } = {};
    mappingDeviceTags.forEach((item) => {
      const tagId = item.tag.id;
      tagCountMap[tagId] = (tagCountMap[tagId] || 0) + 1;
    });

    const deviceTagsForTagList = mappingDeviceTags
      .filter((item) => tagCountMap[item.tag.id] === selectedDeviceIds?.length)
      .map((item) => ({
        label: item.tag.tagValue,
        id: item.tag.id,
      }))
      .filter(
        (tag, index, self) =>
          index ===
          self.findIndex((t) => {
            const seltTagValue = typeof t === "string" ? t : t.label;
            const compareTagValue = typeof tag === "string" ? tag : tag.label;
            return seltTagValue === compareTagValue;
          })
      );
    setDeviceTagsForTagsList(deviceTagsForTagList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsForListData]);

  const handleCloseTagsModal = () => {
    onClose();
    resetCreateDeviceTags();
    resetAssignedTagError();
    setDeviceTagsForTagsList(undefined);
  };

  const handleAddAndDelete = async () => {
    setIsLoading(true);
    await handleCreateDeviceTags({
      mappingDeviceTags: mappingDeviceTagsData?.mappingDeviceTags?.items ?? [],
    });
    if (tagIdsToDelete.length > 0) {
      await handleDeleteDeviceTags({
        mappingDeviceTags: mappingDeviceTagsData?.mappingDeviceTags?.items ?? [],
        ids: tagIdsToDelete,
      });
    }
    setDeviceTagsForTagsList(undefined);
    resetCreateDeviceTags();
    resetTagIdsToDelete();
    await afterMutation();
    if (refetchTagsForLists) await refetchTagsForLists();
    if (refetchTags) await refetchTags();
    handleCloseTagsModal();
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const tagModalLoading =
    (createDeviceTagsLoading ||
      deleteDeviceTagsLoading ||
      tagsLoading ||
      deviceTagsLoading ||
      mappingDeviceTagsLoading ||
      loading) ??
    isLoading ??
    false;
  return { handleAddAndDelete, tagModalLoading, deviceTagsData, handleCloseTagsModal };
};

export default useHandleUpdateDeviceTags;
